import { useEffect, useState, MouseEvent } from "react";
import { useTitanApp } from "@/hooks/useTitanApp";
import {
  generateCertificate,
  addPinCode,
  addDigitalSignature,
  deleteDigitalSignature,
  deleteDigitalCertificate,
  getUsersProfile,
} from "@/dispatchers/memberDispatchers";

// #region Interfaces
export interface DigitalCertificatesProfileTabProps {
  open: boolean;
  activeTab: string;
  setErrorModalOpen: (error: boolean) => void;
  setDeleteModalOpen: (open: boolean) => void;
  setErrorMessage: (message: string) => void;
  setDeleteAction;
  setDeleteMessage: (message: string) => void;
}
interface DigitalCertificate {
  id: string;
  commonName: string;
  serialNumber: string;
  issueDate: string;
  expiryDate: string;
  status: "active" | "expired" | "revoked" | "pending";
  publicKey: string;
  fingerprint: string;
  issuer: string;
  purpose: "prescription" | "general";
}

interface UserCertificateData {
  _id: string;
  commonName: string;
  serialNumber: string;
  issueDate: string;
  expiryDate: string;
  publicKey: string;
  fingerPrint: string;
  issuer: string;
}
interface PrescriptionPIN {
  id: string;
  isSet: boolean;
  createdDate: string;
  lastUsed: string | null;
  usageCount: number;
  isTemporarilyLocked: boolean;
  lockUntil: string | null;
  failedAttempts: number;
}
export interface DigitalCertificatesProfileDataProps {
  open?: boolean;
  activeTab?: string;
  setErrorModalOpen?: (error: boolean) => void;
  setDeleteModalOpen?: (error: boolean) => void;
  setErrorMessage?: (message: string) => void;
  setDeleteAction?: () => void;
  setDeleteMessage?: (message: string) => void;
}
// #endregion Interfaces

export const useDigitalCertificatesProfileData = ({
  open,
  activeTab,
  setErrorModalOpen,
  setDeleteModalOpen,
  setErrorMessage,
  setDeleteAction,
  setDeleteMessage,
}: DigitalCertificatesProfileDataProps = {}) => {
  // Font options for typed signatures
  const fontOptions = [
    { value: "cursive", label: "Cursive", family: "cursive" },
    { value: "script", label: "Script", family: "'Dancing Script', cursive" },
    { value: "elegant", label: "Elegant", family: "'Great Vibes', cursive" },
    { value: "modern", label: "Modern", family: "'Satisfy', cursive" },
    { value: "classic", label: "Classic", family: "'Allura', cursive" },
  ];
  const { appState, memberState } = useTitanApp();
  const { darkMode } = appState || {};
  const {
    currentMember,
    isAddDigitalSignatureLoading,
    isDeleteDigitalSignatureLoading,
    isPinCodeLoading,
    isCertificateLoading,
    userProfiles,
  } = memberState || {};
  const userSignature = userProfiles?.digitalSignatures?.signature || "";
  const userDigitalCertificate =
    userProfiles?.digitalSignatures?.userCertificate;
  const isDarkMode = darkMode ?? false;
  const [certificates, setCertificates] = useState<DigitalCertificate[]>([]);
  const [signatureData, setSignatureData] = useState({
    mode: "draw" as "draw" | "type",
    drawnSignature: null as string | null,
    typedName: "",
    selectedFont: "cursive",
    fontSize: 32,
  });
  const [certificateRequest, setCertificateRequest] = useState({
    isGenerating: false,
    step: 1, // 1: Info, 2: Verification, 3: Generation, 4: Complete
    personalInfo: {
      fullName: "",
      registrationNumber: "",
      organization: "",
      email: "",
    },
    verification: {
      identityDocument: null as File | null,
      registrationCertificate: null as File | null,
    },
  });

  const [prescriptionPIN, setPrescriptionPIN] = useState<PrescriptionPIN>({
    id: "pin-001",
    isSet: userProfiles?.digitalSignatures?.pinCode ? true : false,
    createdDate: "",
    lastUsed: null,
    usageCount: 0,
    isTemporarilyLocked: false,
    lockUntil: null,
    failedAttempts: 0,
  });

  const [pinSetup, setPinSetup] = useState({
    showSetup: false,
    currentPin: userProfiles?.digitalSignatures?.pinCode,
    newPin: "",
    confirmPin: "",
    showPin: false,
    step: 1, // 1: Enter new PIN, 2: Confirm PIN, 3: Success
  });
  const [canvasElement, setCanvasElement] = useState<HTMLCanvasElement | null>(
    null,
  );
  const [isDrawing, setIsDrawing] = useState(false);
  // #endregion

  // #region  UseEffect
  useEffect(() => {
    if (!open || activeTab !== "signature") return;
    if (!canvasElement) return;

    const canvas = canvasElement;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // set canvas size
    canvas.width = 500;
    canvas.height = 200;

    // always clear background first
    ctx.fillStyle = isDarkMode ? "#374151" : "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // if no signature, stop
    if (!userSignature || !userSignature.trim()) {
      return;
    }

    // if signature is an image (base64)
    if (userSignature.startsWith("data:image")) {
      const img = new Image();
      img.onload = () => {
        // clear again to be safe
        ctx.fillStyle = isDarkMode ? "#374151" : "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = userSignature;
    } else {
      // text signature
      ctx.font = "32px cursive";
      ctx.fillStyle = isDarkMode ? "#ffffff" : "#000000";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(userSignature, canvas.width / 2, canvas.height / 2);
    }
  }, [open, activeTab, userSignature, isDarkMode, canvasElement]);

  useEffect(() => {
    if (userProfiles?.digitalSignatures?.userCertificate) {
      const c = userProfiles?.digitalSignatures
        ?.userCertificate as UserCertificateData;
      const mappedCertificate: DigitalCertificate = {
        id: c._id || "",
        commonName: c.commonName || "",
        serialNumber: c.serialNumber || "",
        issueDate: c.issueDate || "",
        expiryDate: c.expiryDate || "",
        status: getCertificateStatus(c.expiryDate),
        publicKey: c.publicKey || "",
        fingerprint: c.fingerPrint || "",
        issuer: c.issuer || "",
        purpose: "prescription" as const,
      };

      setCertificates([mappedCertificate]);
    } else {
      setCertificates([]);
    }
  }, [userProfiles?.digitalSignatures?.userCertificate, isCertificateLoading]);
  // #endregion UseEffect

  // #region Canvas drawing functions
  const startDrawing = (e: MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasElement;
    if (!canvas) return;

    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };

  const draw = (e: MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasElement;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.strokeStyle = isDarkMode ? "#ffffff" : "#000000";
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };
  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasElement;
      if (canvas) {
        const dataURL = canvas.toDataURL();
        setSignatureData((prev) => ({ ...prev, drawnSignature: dataURL }));
      }
    }
  };

  const clearCanvas = () => {
    const canvas = canvasElement;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setSignatureData((prev) => ({ ...prev, drawnSignature: null }));
      }
    }
  };

  const saveDigitalSignature = async (signature: string) => {
    await addDigitalSignature({
      digitalSignature: signature,
      emailAddress: currentMember?.emailAddress || "",
    });
  };

  const handleSaveSignature = () => {
    if (signatureData.mode === "draw" && signatureData.drawnSignature) {
      // Ensure it has the proper data URL format
      const signature = signatureData.drawnSignature.startsWith("data:")
        ? signatureData.drawnSignature
        : `data:image/png;base64,${signatureData.drawnSignature}`;
      saveDigitalSignature(signature);
    } else if (signatureData.mode === "type" && signatureData.typedName) {
      saveDigitalSignature(signatureData.typedName);
    }
  };
  const handleDeleteSignature = () => {
    if (signatureData.mode === "draw" && signatureData.drawnSignature) {
      deleteDigitalSignature(currentMember?.emailAddress);
      setDeleteModalOpen(false);
      clearCanvas();
    }
  };
  const onClearSignature = () => {
    setDeleteModalOpen(true);
    setDeleteAction(() => handleDeleteSignature);
    setDeleteMessage("Are you sure you want to clear your digital signature? ");
  };
  // #endregion

  // #region PIN management functions
  const validatePIN = (pin: string): { valid: boolean; message: string } => {
    if (pin.length !== 4) {
      return { valid: false, message: "PIN must be exactly 4 digits" };
    }
    if (!/^\d+$/.test(pin)) {
      return { valid: false, message: "PIN must contain only numbers" };
    }
    if (pin === "0000" || pin === "1234" || pin === "1111" || pin === "2222") {
      return {
        valid: false,
        message: "PIN is too simple. Choose a different combination",
      };
    }
    return { valid: true, message: "" };
  };

  const setupPIN = async () => {
    const validation = validatePIN(pinSetup.newPin);
    if (!validation.valid) return;

    if (pinSetup.newPin !== pinSetup.confirmPin) return;

    // Call your API method to save PIN to database
    await createPin(pinSetup.newPin);

    // Update local state
    setPrescriptionPIN((prev) => ({
      ...prev,
      isSet: true,
      createdDate: new Date().toISOString().split("T")[0],
    }));

    setPinSetup({
      showSetup: false,
      currentPin: "",
      newPin: "",
      confirmPin: "",
      showPin: false,
      step: 3,
    });

    // Reset to step 1 after showing success
    setTimeout(() => {
      setPinSetup((prev) => ({ ...prev, step: 1 }));
    }, 2000);
  };

  const changePIN = async () => {
    // In a real app, this would verify the current PIN first
    const validation = validatePIN(pinSetup.newPin);
    if (!validation.valid) return;

    if (pinSetup.newPin !== pinSetup.confirmPin) return;

    // Call your API method to update PIN in database
    await createPin(pinSetup.newPin);

    // Update PIN
    setPrescriptionPIN((prev) => ({
      ...prev,
      createdDate: new Date().toISOString().split("T")[0],
    }));
    setPinSetup((prev) => ({
      showSetup: false,
      currentPin: prev.newPin,
      newPin: "",
      confirmPin: "",
      showPin: false,
      step: 3,
    }));

    setTimeout(() => {
      setPinSetup((prev) => ({ ...prev, step: 1 }));
    }, 2000);
  };

  const resetPinSetup = () => {
    setPinSetup((prev) => ({
      showSetup: false,
      currentPin: prev.currentPin,
      newPin: "",
      confirmPin: "",
      showPin: false,
      step: 1,
    }));
  };
  const onRemovePIN = () => {
    setDeleteModalOpen(true);
    setDeleteAction(() => removePIN);
    setDeleteMessage(
      "Are you sure you want to remove your Prescription Sign-Off Pin?",
    );
  };

  const removePIN = async () => {
    try {
      // Call your API method to remove PIN (empty string means remove)
      await createPin("");

      // Update local state to indicate PIN is removed
      setPrescriptionPIN((prev) => ({
        ...prev,
        isSet: false,
        createdDate: "",
        lastUsed: null,
        usageCount: 0,
        failedAttempts: 0,
      }));

      // Show success step like setup/change
      setPinSetup({
        showSetup: false,
        currentPin: "",
        newPin: "",
        confirmPin: "",
        showPin: false,
        step: 3,
      });

      // Reset back to step 1 after showing success
      setTimeout(() => {
        setPinSetup((prev) => ({ ...prev, step: 1 }));
      }, 2000);
    } catch (error) {
      // optionally show error to user
      console.error("Failed to remove PIN:", error);
    }
  };
  const createPin = async (pin: string) => {
    await addPinCode({
      pinCode: pin,
      emailAddress: currentMember?.emailAddress || "",
    });
  };
  // #endregion

  // #region Certificate generation functions
  const generateNewCertificate = async () => {
    setCertificates([]);
    const response = await generateCertificate();
    if (!response?.success) {
      setErrorModalOpen(true);
      setErrorMessage(response?.message || "Failed to generate certificate");
    }
  };
  const resetCertificateRequest = () => {
    setCertificateRequest({
      isGenerating: false,
      step: 1,
      personalInfo: {
        fullName: "",
        registrationNumber: "",
        organization: "",
        email: "",
      },
      verification: {
        identityDocument: null,
        registrationCertificate: null,
      },
    });
  };

  const revokeCertificate = (certId: string) => {
    deleteDigitalCertificate(currentMember?.emailAddress).then(async () => {
      await getUsersProfile();
    });
    setCertificates([]);
  };

  const downloadCertificate = (cert: DigitalCertificate) => {
    // In a real app, this would download the actual certificate file
    const certData = {
      id: cert.id,
      commonName: cert.commonName,
      serialNumber: cert.serialNumber,
      issueDate: cert.issueDate,
      expiryDate: cert.expiryDate,
      publicKey: cert.publicKey,
      fingerprint: cert.fingerprint,
      issuer: cert.issuer,
    };

    const blob = new Blob([JSON.stringify(certData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `certificate-${cert.serialNumber}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Helper function to determine certificate status based on expiry date
  const getCertificateStatus = (
    expiryDate: string,
  ): "active" | "expired" | "revoked" | "pending" => {
    const expiry = new Date(expiryDate);
    const now = new Date();

    if (expiry < now) {
      return "expired";
    }
    return "active";
  };
  // #endregion

  // #region Return
  return {
    isDarkMode,
    fontOptions,
    certificateRequest,
    setCertificateRequest,
    downloadCertificate,
    revokeCertificate,
    prescriptionPIN,
    pinSetup,
    setPinSetup,
    onRemovePIN,
    validatePIN,
    signatureData,
    setSignatureData,
    setCanvasElement,
    startDrawing,
    draw,
    stopDrawing,
    clearCanvas,
    handleSaveSignature,
    onClearSignature,
    generateNewCertificate,
    isCertificateLoading,
    certificates,
    resetCertificateRequest,
    setCertificates,
    createPin,
    changePIN,
    resetPinSetup,
    setupPIN,
    isPinCodeLoading,
    isAddDigitalSignatureLoading,
    isDeleteDigitalSignatureLoading,
    userSignature,
    setErrorModalOpen,
    setDeleteModalOpen,
    userDigitalCertificate,
  };
  //  #endregion Return
};
